// components/pdf/PDFViewer.tsx - Fixed with working two-level highlighting
import React, { useState, useCallback, memo, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface TermInstance {
  _id: string;
  document_id: string;
  term_id: string;
  matched_text: string;
  page_number: number;
  confidence_score: number;
  context?: string;
  highlighting_data?: {
    position_in_page: number;
    page_start_position: number;
    page_end_position: number;
    corpus_start_in_page: number;
    corpus_end_in_page: number;
    corpus_content: string;
    corpus_length: number;
    term_start_in_corpus: number;
    term_end_in_corpus: number;
    term_start_in_page: number;
    term_end_in_page: number;
    term_length: number;
    highlighting_quality?: {
      corpus_size_appropriate: boolean;
      position_accuracy: string;
      boundary_detection: string;
    };
  };
}

interface Term {
  _id: string;
  term: string;
  category: string;
  risk_level: string;
  industry_definition: string;
}

interface PDFViewerProps {
  documentId: string;
  selectedTerm?: Term | null;
  termInstances: TermInstance[];
  currentPage: number;
  onPageChange: (page: number) => void;
  zoomLevel: number;
  highlightedInstanceIndex: number;
  onLoadSuccess?: (numPages: number) => void;
}

// Simplified and Working Text Highlighter
const WorkingTextHighlighter: React.FC<{
  selectedTerm: Term | null;
  termInstances: TermInstance[];
  currentPage: number;
  highlightedInstanceIndex: number;
}> = ({ selectedTerm, termInstances, currentPage, highlightedInstanceIndex }) => {
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!selectedTerm) {
      clearExistingHighlights();
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Get instances for current page and term
    const pageInstances = termInstances.filter(
      instance => instance.term_id === selectedTerm._id && instance.page_number === currentPage
    );

    if (pageInstances.length === 0) {
      clearExistingHighlights();
      return;
    }

    // Get the current instance to highlight
    const currentInstance = pageInstances[highlightedInstanceIndex % pageInstances.length];
    if (!currentInstance) {
      clearExistingHighlights();
      return;
    }

    console.log('🎨 Highlighting instance:', {
      term: selectedTerm.term,
      page: currentPage,
      hasEnhancedData: !!currentInstance.highlighting_data,
      matchedText: currentInstance.matched_text
    });

    // Use timeout to ensure text layer is ready
    timeoutRef.current = setTimeout(() => {
      applyHighlighting(currentInstance);
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [selectedTerm, termInstances, currentPage, highlightedInstanceIndex]);

  const clearExistingHighlights = () => {
    const existingHighlights = document.querySelectorAll('.pdf-corpus-highlight, .pdf-term-highlight, .simple-highlight');
    existingHighlights.forEach(highlight => {
      const parent = highlight.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(highlight.textContent || ''), highlight);
        parent.normalize();
      }
    });
  };

  const applyHighlighting = (instance: TermInstance) => {
    const textLayer = document.querySelector('.react-pdf__Page__textContent');
    if (!textLayer) {
      console.log('⚠️ Text layer not found');
      return;
    }

    console.log('📄 Text layer found, applying highlighting...');
    clearExistingHighlights();

    // Check if we have enhanced highlighting data
    if (instance.highlighting_data) {
      console.log('🎨 Using enhanced highlighting data');
      applyEnhancedHighlighting(textLayer, instance);
    } else {
      console.log('🔍 Using simple highlighting fallback');
      applySimpleHighlighting(textLayer, instance);
    }
  };

  const applyEnhancedHighlighting = (textLayer: Element, instance: TermInstance) => {
    const highlightData = instance.highlighting_data!;
    const spans = Array.from(textLayer.querySelectorAll('span')) as HTMLElement[];
    
    if (spans.length === 0) {
      console.log('⚠️ No text spans found');
      return;
    }

    // Get all text content to work with
    let fullText = '';
    const spanMap: Array<{ span: HTMLElement; start: number; end: number; text: string }> = [];
    
    spans.forEach(span => {
      const text = span.textContent || '';
      const start = fullText.length;
      const end = start + text.length;
      spanMap.push({ span, start, end, text });
      fullText += text;
    });

    console.log('📊 Text analysis:', {
      totalLength: fullText.length,
      corpusStart: highlightData.corpus_start_in_page,
      corpusEnd: highlightData.corpus_end_in_page,
      termStart: highlightData.term_start_in_page,
      termEnd: highlightData.term_end_in_page
    });

    // First pass: Apply corpus highlighting (light yellow)
    spanMap.forEach(({ span, start, end, text }) => {
      if (end >= highlightData.corpus_start_in_page && start <= highlightData.corpus_end_in_page) {
        const overlapStart = Math.max(start, highlightData.corpus_start_in_page);
        const overlapEnd = Math.min(end, highlightData.corpus_end_in_page);
        
        if (overlapStart < overlapEnd) {
          const beforeText = text.substring(0, overlapStart - start);
          const corpusText = text.substring(overlapStart - start, overlapEnd - start);
          const afterText = text.substring(overlapEnd - start);
          
          span.innerHTML = '';
          
          if (beforeText) {
            span.appendChild(document.createTextNode(beforeText));
          }
          
          if (corpusText) {
            const corpusElement = document.createElement('span');
            corpusElement.className = 'pdf-corpus-highlight';
            corpusElement.style.cssText = `
              background-color: rgba(255, 255, 0, 0.3);
              padding: 2px 0;
              border-radius: 2px;
              display: inline;
            `;
            corpusElement.textContent = corpusText;
            span.appendChild(corpusElement);
          }
          
          if (afterText) {
            span.appendChild(document.createTextNode(afterText));
          }
        }
      }
    });

    // Second pass: Apply term highlighting (dark yellow) within corpus
    setTimeout(() => {
      const corpusHighlights = textLayer.querySelectorAll('.pdf-corpus-highlight');
      const searchTerm = instance.matched_text.toLowerCase();
      
      corpusHighlights.forEach(corpusElement => {
        const corpusText = corpusElement.textContent || '';
        const lowerCorpusText = corpusText.toLowerCase();
        const termIndex = lowerCorpusText.indexOf(searchTerm);
        
        if (termIndex !== -1) {
          const beforeTerm = corpusText.substring(0, termIndex);
          const termText = corpusText.substring(termIndex, termIndex + searchTerm.length);
          const afterTerm = corpusText.substring(termIndex + searchTerm.length);
          
          corpusElement.innerHTML = '';
          
          if (beforeTerm) {
            corpusElement.appendChild(document.createTextNode(beforeTerm));
          }
          
          const termElement = document.createElement('span');
          termElement.className = 'pdf-term-highlight';
          termElement.style.cssText = `
            background-color: rgba(255, 193, 7, 0.9);
            color: #000;
            padding: 2px 4px;
            border-radius: 3px;
            font-weight: bold;
            box-shadow: 0 2px 4px rgba(255, 193, 7, 0.4);
            animation: term-pulse 2s ease-in-out;
            position: relative;
            z-index: 10;
          `;
          termElement.textContent = termText;
          corpusElement.appendChild(termElement);
          
          if (afterTerm) {
            corpusElement.appendChild(document.createTextNode(afterTerm));
          }
          
          // Scroll to highlighted term
          setTimeout(() => {
            termElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center'
            });
          }, 100);
        }
      });
    }, 100);
  };

  const applySimpleHighlighting = (textLayer: Element, instance: TermInstance) => {
    const spans = textLayer.querySelectorAll('span');
    const searchTerm = instance.matched_text.toLowerCase();
    let highlighted = false;
    
    spans.forEach(span => {
      const text = span.textContent || '';
      const lowerText = text.toLowerCase();
      
      if (lowerText.includes(searchTerm)) {
        const index = lowerText.indexOf(searchTerm);
        if (index !== -1) {
          const beforeText = text.substring(0, index);
          const matchText = text.substring(index, index + searchTerm.length);
          const afterText = text.substring(index + searchTerm.length);
          
          span.innerHTML = '';
          
          if (beforeText) {
            span.appendChild(document.createTextNode(beforeText));
          }
          
          const highlight = document.createElement('span');
          highlight.className = 'simple-highlight';
          highlight.style.cssText = `
            background-color: rgba(255, 235, 59, 0.8);
            color: #000;
            padding: 2px 4px;
            border-radius: 3px;
            font-weight: bold;
            box-shadow: 0 2px 4px rgba(255, 193, 7, 0.3);
            animation: simple-pulse 2s ease-in-out;
          `;
          highlight.textContent = matchText;
          span.appendChild(highlight);
          
          if (afterText) {
            span.appendChild(document.createTextNode(afterText));
          }
          
          if (!highlighted) {
            highlighted = true;
            setTimeout(() => {
              highlight.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center'
              });
            }, 100);
          }
        }
      }
    });
  };

  return null;
};

// Add required CSS styles
const HighlightingStyles = () => (
  <style>{`
    @keyframes term-pulse {
      0% { 
        box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.7);
        transform: scale(1);
      }
      50% { 
        box-shadow: 0 0 0 8px rgba(255, 193, 7, 0);
        transform: scale(1.05);
      }
      100% { 
        box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
        transform: scale(1);
      }
    }
    
    @keyframes simple-pulse {
      0% { 
        box-shadow: 0 0 0 0 rgba(255, 235, 59, 0.7);
      }
      50% { 
        box-shadow: 0 0 0 6px rgba(255, 235, 59, 0);
      }
      100% { 
        box-shadow: 0 0 0 0 rgba(255, 235, 59, 0);
      }
    }
    
    .pdf-corpus-highlight {
      transition: all 0.3s ease;
    }
    
    .pdf-term-highlight {
      transition: all 0.3s ease;
    }
    
    .simple-highlight {
      transition: all 0.3s ease;
    }
  `}</style>
);

// MEMOIZED PDF DOCUMENT COMPONENT
const StablePDFDocument = memo(({ 
  pdfUrl, 
  currentPage, 
  zoomLevel, 
  onLoadSuccess, 
  onLoadError,
  selectedTerm,
  termInstances,
  highlightedInstanceIndex
}: {
  pdfUrl: string;
  currentPage: number;
  zoomLevel: number;
  onLoadSuccess: (data: { numPages: number }) => void;
  onLoadError: (error: Error) => void;
  selectedTerm: Term | null;
  termInstances: TermInstance[];
  highlightedInstanceIndex: number;
}) => {
  const [pageLoaded, setPageLoaded] = useState(false);

  const handlePageLoadSuccess = useCallback(() => {
    console.log('✅ Page loaded successfully');
    setPageLoaded(true);
  }, []);

  return (
    <>
      <HighlightingStyles />
      <Document
        file={pdfUrl}
        onLoadSuccess={onLoadSuccess}
        onLoadError={onLoadError}
        loading={
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '500px',
            backgroundColor: '#f8f9fa',
            border: '2px dashed #007bff',
            borderRadius: '8px'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e3e3e3',
              borderTop: '4px solid #007bff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '20px'
            }}></div>
            <h3 style={{ color: '#007bff', marginBottom: '10px' }}>Loading PDF Document</h3>
            <p style={{ color: '#666', margin: '0' }}>Please wait while we load your contract...</p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        }
        error={
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '500px',
            backgroundColor: '#fff5f5',
            border: '2px solid #dc3545',
            borderRadius: '8px',
            color: '#dc3545',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>📄❌</div>
            <h3>Failed to Load PDF</h3>
            <p>Unable to load the contract document</p>
          </div>
        }
        options={{
          cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
          cMapPacked: true,
          standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '20px',
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{ position: 'relative' }}>
            <Page
              pageNumber={currentPage}
              scale={zoomLevel / 100}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              onLoadSuccess={handlePageLoadSuccess}
              loading={
                <div style={{
                  height: '600px',
                  width: '450px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'white',
                  border: '2px dashed #dee2e6',
                  borderRadius: '8px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      width: '30px',
                      height: '30px',
                      border: '3px solid #f3f3f3',
                      borderTop: '3px solid #007bff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto 10px'
                    }}></div>
                    <p>Loading page {currentPage}...</p>
                  </div>
                </div>
              }
              error={
                <div style={{
                  height: '600px',
                  width: '450px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fff5f5',
                  border: '2px solid #dc3545',
                  borderRadius: '8px',
                  color: '#dc3545'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <p>Error loading page {currentPage}</p>
                  </div>
                </div>
              }
            />
            
            {/* Working Text Highlighter */}
            {pageLoaded && (
              <WorkingTextHighlighter
                selectedTerm={selectedTerm}
                termInstances={termInstances}
                currentPage={currentPage}
                highlightedInstanceIndex={highlightedInstanceIndex}
              />
            )}
          </div>
        </div>
      </Document>
    </>
  );
});

const PDFViewer: React.FC<PDFViewerProps> = ({
  documentId,
  selectedTerm,
  termInstances,
  currentPage,
  onPageChange,
  zoomLevel,
  highlightedInstanceIndex,
  onLoadSuccess
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pdfUrl = `http://localhost:8000/api/documents/${documentId}/pdf`;

  // Document load success handler
  const handleLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    console.log('✅ PDF loaded successfully:', numPages, 'pages');
    setNumPages(numPages);
    setLoading(false);
    setError(null);
    
    if (onLoadSuccess) {
      onLoadSuccess(numPages);
    }
  }, [onLoadSuccess]);

  // Document load error handler
  const handleLoadError = useCallback((error: Error) => {
    console.error('❌ PDF load error:', error);
    setError(`Failed to load PDF: ${error.message}`);
    setLoading(false);
  }, []);

  // Get current page instances for display info
  const getCurrentPageInstances = useCallback(() => {
    if (!selectedTerm) return [];
    return termInstances.filter(
      instance => instance.term_id === selectedTerm._id && instance.page_number === currentPage
    );
  }, [selectedTerm, termInstances, currentPage]);

  // Check if current page instances have highlighting data
  const getHighlightingDataStatus = useCallback(() => {
    const pageInstances = getCurrentPageInstances();
    const withHighlightingData = pageInstances.filter(instance => instance.highlighting_data);
    
    return {
      total: pageInstances.length,
      enhanced: withHighlightingData.length,
      hasEnhanced: withHighlightingData.length > 0
    };
  }, [getCurrentPageInstances]);

  // Loading state
  if (loading) {
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <StablePDFDocument
          pdfUrl={pdfUrl}
          currentPage={currentPage}
          zoomLevel={zoomLevel}
          onLoadSuccess={handleLoadSuccess}
          onLoadError={handleLoadError}
          selectedTerm={selectedTerm || null}
          termInstances={termInstances}
          highlightedInstanceIndex={highlightedInstanceIndex}
        />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        backgroundColor: '#fff5f5',
        padding: '40px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '64px', marginBottom: '25px', color: '#dc3545' }}>
          📄💥
        </div>
        <h3 style={{ color: '#dc3545', marginBottom: '20px' }}>
          PDF Loading Failed
        </h3>
        <p style={{ 
          marginBottom: '25px', 
          maxWidth: '500px', 
          lineHeight: '1.6',
          color: '#495057'
        }}>
          {error}
        </p>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              setError(null);
              setLoading(true);
            }}
            style={{
              padding: '12px 24px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            🔄 Retry Loading
          </button>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '12px 24px',
              backgroundColor: '#28a745',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            🔗 Open Direct
          </a>
        </div>
      </div>
    );
  }

  // Success state - PDF is loaded and ready
  const highlightStatus = getHighlightingDataStatus();

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* PDF Document with Working Two-Level Highlighting */}
      <StablePDFDocument
        pdfUrl={pdfUrl}
        currentPage={currentPage}
        zoomLevel={zoomLevel}
        onLoadSuccess={handleLoadSuccess}
        onLoadError={handleLoadError}
        selectedTerm={selectedTerm || null}
        termInstances={termInstances}
        highlightedInstanceIndex={highlightedInstanceIndex}
      />



      {/* Document info bar with highlighting status */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '20px',
        right: '20px',
        backgroundColor: 'rgba(0,0,0,0.85)',
        color: 'white',
        padding: '12px 25px',
        borderRadius: '30px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '13px',
        zIndex: 100,
        boxShadow: '0 4px 15px rgba(0,0,0,0.3)'
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          📄 Page {currentPage} of {numPages} • 🔍 {zoomLevel}% zoom
        </span>
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ 
            color: '#4fc3f7', 
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          🔗 Open in new tab
        </a>
      </div>
    </div>
  );
};

export default PDFViewer;